import { getAccessToken } from './auth.service.js'

async function fetchHelper(url, options = {}, responseType = 'json') {
  const token = await getAccessToken()

  const response = await fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    throw new Error(response.statusText)
  }

  if (responseType === 'blob') return response.blob()
  if (responseType === 'arrayBuffer') return response.arrayBuffer()
  const data = await response.json()
  return { data }
}

async function fetchAllMissionProjects() {
  const { data } = await fetchHelper('/api/missions/fetchAll', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })
  return data
}

async function fetchAllSources() {
  const { data } = await fetchHelper('/api/sources/fetchAll', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })
  return data
}

async function fetchAllMissionDataTypes() {
  const { data } = await fetchHelper('/api/missionDataTypes/fetchAll', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })
  return data
}

async function fetchMissionDataTypesByGroup(group, mission) {
  const url = `/api/missionDataTypes/fetchByGroup${mission ? `/${mission}` : ''}/${group}`
  const { data } = await fetchHelper(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })
  return data
}

async function fetchMissionDataSourcesByGroup(mission, group) {
  const { data } = await fetchHelper(`/api/sources/fetchMissionDataSourcesByGroup/${mission}/${group}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })
  return data
}

async function fetchMissionDataByProps(mission, group, sources, types) {
  let url = `/api/missionData/fetchByProps/${mission}/${group}`

  if (sources) {
    url += `?source=${sources.join(',')}`
  }

  if (types) {
    url += `${sources ? '&' : '?'}type=${types.join(',')}`
  }

  const { data } = await fetchHelper(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })
  return data
}

async function createMissionData(data) {
  return fetchHelper(`/api/missionData/create`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

async function deleteMissionData(id) {
  return fetchHelper(`/api/missionData/delete/${id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
  })
}

async function fetchMissionSamplingStrategies(missionId) {
  const { data } = await fetchHelper(`/api/samplingStrategies/fetchByMission/${missionId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })
  return data
}

async function createMissionSamplingStrategy(strategy) {
  const response = await fetchHelper(`/api/samplingStrategies/create`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(strategy),
  })
  return response
}

async function updateMissionSamplingStrategy(id, strategy) {
  const response = await fetchHelper(`/api/samplingStrategies/update/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(strategy),
  })
  return response
}

async function deleteMissionSamplingStrategy(id) {
  const response = await fetchHelper(`/api/samplingStrategies/delete/${id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
  })
  return response
}

async function downloadMissionSamplingStrategy(id) {
  const { data } = await fetchHelper(`/api/samplingStrategies/download/${id}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })
  return data
}

async function fetchSampleLocationsByMission(missionId) {
  const { data } = await fetchHelper(`/api/samples/fetchByProps/${missionId}?summary=true`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })
  return data
}

async function fetchLabResultsByMission(missionId, sources, mosquitoTypes, startDate, endDate, mosquitoSexActiveIds, analysisTypes) {
  const searchParams = new URLSearchParams()
  if (Array.isArray(sources)) searchParams.append('sources', sources.join(','))
  if (Array.isArray(mosquitoTypes)) searchParams.append('species', mosquitoTypes.join(','))
  if (startDate) searchParams.append('startDate', startDate)
  if (endDate) searchParams.append('endDate', endDate)
  if (Array.isArray(mosquitoSexActiveIds)) searchParams.append('sexes', mosquitoSexActiveIds.join(','))
  if (Array.isArray(analysisTypes)) searchParams.append('analysisTypes', analysisTypes.join(','))

  const queryString = searchParams.toString()
  const endpoint = `/api/samples/fetchLabResults/${missionId}${queryString ? `?${queryString}` : ''}`

  const { data } = await fetchHelper(endpoint, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })
  return data
}

async function fetchLabResultSourcesByMission(missionId, startDate, endDate) {
  const searchParams = new URLSearchParams()
  if (startDate) searchParams.append('startDate', startDate)
  if (endDate) searchParams.append('endDate', endDate)

  const queryString = searchParams.toString()
  const endpoint = `/api/samples/fetchLabResultSources/${missionId}${queryString ? `?${queryString}` : ''}`

  const { data } = await fetchHelper(endpoint, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })
  return data
}

async function fetchSightingsByMission(
  missionId,
  startDate,
  endDate,
  sourcesActiveIds,
  mosquitoSexActiveIds,
  mosquitoTypes,
) {
  const searchParams = new URLSearchParams()
  if (startDate) searchParams.append('startDate', startDate)
  if (endDate) searchParams.append('endDate', endDate)
  if (Array.isArray(sourcesActiveIds)) searchParams.append('sources', sourcesActiveIds.join(','))
  if (Array.isArray(mosquitoSexActiveIds)) searchParams.append('sexes', mosquitoSexActiveIds.join(','))
  if (Array.isArray(mosquitoTypes)) {
    searchParams.append('species', mosquitoTypes.join(','))
  }

  const queryString = searchParams.toString()
  const endpoint = `/api/sightings/fetchByProps/${missionId}${queryString ? `?${queryString}` : ''}`

  const { data } = await fetchHelper(endpoint, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })
  return data
}

async function fetchMosquitoObservationsByProps(missionId, sources, mosquitoTypes, startDate, endDate, mosquitoSexActiveIds, smartTrapFilterIds) {
  const searchParams = new URLSearchParams()
  if (Array.isArray(sources)) searchParams.append('sources', sources.join(','))
  if (Array.isArray(mosquitoTypes)) searchParams.append('species', mosquitoTypes.join(','))
  if (startDate) searchParams.append('startDate', startDate)
  if (endDate) searchParams.append('endDate', endDate)
  if (Array.isArray(mosquitoSexActiveIds)) searchParams.append('sexes', mosquitoSexActiveIds.join(','))
  if (Array.isArray(smartTrapFilterIds)) searchParams.append('statuses', smartTrapFilterIds.join(','))

  const queryString = searchParams.toString()
  const endpoint = `/api/samples/fetchByProps/${missionId}${queryString ? `?${queryString}` : ''}`

  const { data } = await fetchHelper(endpoint, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })
  return data
}

async function fetchUniqueSpeciesByMission(
  missionId,
  startDate,
  endDate,
  sourcesActiveIds,
  mosquitoSexActiveIds,
  smartTrapFilterIds,
) {
  const searchParams = new URLSearchParams()
  if (startDate) searchParams.append('startDate', startDate)
  if (endDate) searchParams.append('endDate', endDate)
  if (Array.isArray(sourcesActiveIds)) {
    searchParams.append('sourcesActiveIds', sourcesActiveIds.join(','))
  }
  if (Array.isArray(mosquitoSexActiveIds)) {
    searchParams.append('mosquitoSexActiveIds', mosquitoSexActiveIds.join(','))
  }
  if (Array.isArray(smartTrapFilterIds)) {
    searchParams.append('statuses', smartTrapFilterIds.join(','))
  }
  const queryString = searchParams.toString()
  const endpoint = `/api/species/fetchUniqueSpecies/${missionId}${queryString ? `?${queryString}` : ''}`

  const { data } = await fetchHelper(endpoint, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })
  return data
}

async function fetchMosquitoSourcesByMission(missionId, startDate, endDate) {
  const searchParams = new URLSearchParams()
  if (startDate) searchParams.append('startDate', startDate)
  if (endDate) searchParams.append('endDate', endDate)
  const queryString = searchParams.toString()
  const endpoint = `/api/sources/fetchMosquitoSourcesByMission/${missionId}${queryString ? `?${queryString}` : ''}`

  const { data } = await fetchHelper(endpoint, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })
  return data
}

export {
  fetchAllMissionProjects,
  fetchAllSources,
  fetchAllMissionDataTypes,

  fetchMissionDataByProps,
  fetchMissionDataTypesByGroup,
  fetchMissionDataSourcesByGroup,
  createMissionData,
  deleteMissionData,

  fetchMissionSamplingStrategies,
  createMissionSamplingStrategy,
  updateMissionSamplingStrategy,
  deleteMissionSamplingStrategy,
  downloadMissionSamplingStrategy,

  fetchSampleLocationsByMission,
  fetchSightingsByMission,
  fetchMosquitoObservationsByProps,
  fetchLabResultsByMission,
  fetchLabResultSourcesByMission,
  fetchUniqueSpeciesByMission,
  fetchMosquitoSourcesByMission,
}
